/**
 * Packaging Configuration Form Component
 * Form for configuring packaging hierarchy (units per case, cases per pallet)
 */

import { useState, useEffect, useMemo } from 'react';
import { Alert, Button, HierarchyTree, Input, type HierarchyTreeNode } from './ui';

export interface PackagingConfigFormData {
  unitsPerCase: number;
  casesPerPallet: number;
  caseDimensions?: string;
  palletDimensions?: string;
}

export interface PackagingConfigFormProps {
  initialData?: PackagingConfigFormData;
  onSubmit: (data: PackagingConfigFormData) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
}

export default function PackagingConfigForm({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
}: PackagingConfigFormProps) {
  const [formData, setFormData] = useState<PackagingConfigFormData>({
    unitsPerCase: initialData?.unitsPerCase || 24,
    casesPerPallet: initialData?.casesPerPallet || 50,
    caseDimensions: initialData?.caseDimensions || '',
    palletDimensions: initialData?.palletDimensions || '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const handleChange = (field: keyof PackagingConfigFormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.unitsPerCase || formData.unitsPerCase < 1) {
      newErrors.unitsPerCase = 'Units per case must be at least 1';
    }

    if (!formData.casesPerPallet || formData.casesPerPallet < 1) {
      newErrors.casesPerPallet = 'Cases per pallet must be at least 1';
    }

    // Validate dimensions format if provided
    const dimensionRegex = /^\d+\s*x\s*\d+\s*x\s*\d+(\s*(cm|in))?$/i;
    if (formData.caseDimensions && !dimensionRegex.test(formData.caseDimensions.trim())) {
      newErrors.caseDimensions = 'Format: L x W x H (e.g., "30 x 20 x 15 cm")';
    }

    if (formData.palletDimensions && !dimensionRegex.test(formData.palletDimensions.trim())) {
      newErrors.palletDimensions = 'Format: L x W x H (e.g., "120 x 100 x 150 cm")';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    await onSubmit({
      ...formData,
      caseDimensions: formData.caseDimensions?.trim() || undefined,
      palletDimensions: formData.palletDimensions?.trim() || undefined,
    });
  };

  const totalUnitsPerPallet = formData.unitsPerCase * formData.casesPerPallet;
  const hierarchyPreviewNodes = useMemo<HierarchyTreeNode[]>(
    () => [
      {
        id: 'pallet',
        label: 'Pallet',
        subtitle: formData.palletDimensions?.trim() || 'Pallet dimensions not set',
        statusLabel: 'Configured',
        statusTone: 'anchored',
        quantityLabel: `${formData.casesPerPallet} cases`,
        qrCountLabel: `${totalUnitsPerPallet} units`,
        children: [
          {
            id: 'case',
            label: 'Case',
            subtitle: formData.caseDimensions?.trim() || 'Case dimensions not set',
            statusLabel: 'Configured',
            statusTone: 'active',
            quantityLabel: `${formData.unitsPerCase} units`,
            children: [
              {
                id: 'unit',
                label: 'Unit',
                subtitle: 'Serialized sellable unit',
                statusLabel: 'Configured',
                statusTone: 'muted',
              },
            ],
          },
        ],
      },
    ],
    [formData.caseDimensions, formData.casesPerPallet, formData.palletDimensions, formData.unitsPerCase, totalUnitsPerPallet]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Info Box */}
      <Alert variant="info">
        <p className="font-medium mb-1">About Packaging Configuration</p>
        <p className="text-sm">
          Define how your products are packaged for bulk shipments. This enables efficient
          case and pallet-level transfers in your supply chain.
        </p>
      </Alert>

      {/* Units per Case */}
      <div>
        <Input
          label="Units per Case"
          type="number"
          min="1"
          value={formData.unitsPerCase}
          onChange={(e) => handleChange('unitsPerCase', parseInt(e.target.value) || 0)}
          error={errors.unitsPerCase}
          helperText="How many individual products fit in one case?"
          required
          disabled={loading}
        />
      </div>

      {/* Cases per Pallet */}
      <div>
        <Input
          label="Cases per Pallet"
          type="number"
          min="1"
          value={formData.casesPerPallet}
          onChange={(e) => handleChange('casesPerPallet', parseInt(e.target.value) || 0)}
          error={errors.casesPerPallet}
          helperText="How many cases fit on one pallet?"
          required
          disabled={loading}
        />
      </div>

      {/* Case Dimensions (Optional) */}
      <div>
        <Input
          label="Case Dimensions (Optional)"
          type="text"
          value={formData.caseDimensions}
          onChange={(e) => handleChange('caseDimensions', e.target.value)}
          error={errors.caseDimensions}
          placeholder="e.g., 30 x 20 x 15 cm"
          helperText="Format: Length x Width x Height"
          disabled={loading}
        />
      </div>

      {/* Pallet Dimensions (Optional) */}
      <div>
        <Input
          label="Pallet Dimensions (Optional)"
          type="text"
          value={formData.palletDimensions}
          onChange={(e) => handleChange('palletDimensions', e.target.value)}
          error={errors.palletDimensions}
          placeholder="e.g., 120 x 100 x 150 cm"
          helperText="Format: Length x Width x Height"
          disabled={loading}
        />
      </div>

      {/* Preview Calculation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-3">Configuration Preview</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-blue-700 font-medium">Per Case</p>
            <p className="text-2xl font-bold text-blue-900">{formData.unitsPerCase}</p>
            <p className="text-blue-600">units</p>
          </div>
          <div>
            <p className="text-blue-700 font-medium">Per Pallet</p>
            <p className="text-2xl font-bold text-blue-900">{formData.casesPerPallet}</p>
            <p className="text-blue-600">cases</p>
          </div>
          <div>
            <p className="text-blue-700 font-medium">Total per Pallet</p>
            <p className="text-2xl font-bold text-blue-900">{totalUnitsPerPallet}</p>
            <p className="text-blue-600">units</p>
          </div>
        </div>
        <div className="mt-4">
          <HierarchyTree nodes={hierarchyPreviewNodes} />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : initialData ? 'Update Configuration' : 'Save Configuration'}
        </Button>
      </div>
    </form>
  );
}
